/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { ButtonIcon } from "../../components/Button/Button"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faCalendarDay,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faClipboardCheck,
  faClose,
  faFlagCheckered,
  faFont,
  faHashtag,
  faListCheck,
  faRotateLeft,
  faThumbsDown,
  faThumbsUp,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import InputField from "../../components/InputField/InputField"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { useChecklist } from "../../hooks/Checklist/useChecklist"
import { useConfirmation } from "../../context/useConfirmationContext"
import type { ChecklistExecution } from "../../Objects/Checklist/ChecklistExecution"
import type { ChecklistExecutionStep } from "../../Objects/Checklist/ChecklistExecutionStep"
import { describeChecklistExecutionStepValue } from "../../Objects/Checklist/ChecklistExecutionStepFormat"
import { companyMemory } from "../../Objects/Memory/Memory"
import { formatDateTimeInTimeZone } from "../../utils/timeZone"

interface Props {
  templateId?: number
  executionId?: number
  onClose: () => void
  onChanged: () => void
}

const RESPONSE_TYPE_ICON: Record<ChecklistExecutionStep["responseType"], IconProp> = {
  Confirmation: faCheck,
  YesNo: faThumbsUp,
  MultipleChoice: faListCheck,
  Text: faFont,
  Number: faHashtag,
  DateTime: faCalendarDay,
}

export const ChecklistRunPopup: React.FC<Props> = ({ templateId, executionId, onClose, onChanged }) => {
  const { t } = useTranslation(["pages", "general"])

  const { startExecution, getExecution, updateExecutionStep, completeExecution, isLoading } = useChecklist()

  const { setNewConfirmation } = useConfirmation()

  const [execution, setExecution] = useState<ChecklistExecution | null>(null)

  const [currentIndex, setCurrentIndex] = useState(0)

  const [showNotCompletedForm, setShowNotCompletedForm] = useState(false)

  const [notCompletedReason, setNotCompletedReason] = useState("")

  useEffect(() => {
    void (async () => {
      const result = executionId
        ? await getExecution(executionId)
        : templateId
          ? await startExecution(templateId)
          : undefined

      if (result) {
        setExecution(result)

        const firstPending = result.steps.findIndex((step) => step.status === "Pending")

        setCurrentIndex(firstPending === -1 ? Math.max(result.steps.length - 1, 0) : firstPending)
      }
    })()
  }, [templateId, executionId])

  const steps = execution?.steps ?? []

  const totalSteps = steps.length

  const currentStep = steps[currentIndex]

  const isLastStep = currentIndex === totalSteps - 1

  const isFinalized = execution?.status === "Completed"

  const firstPendingIndex = steps.findIndex((step) => step.status === "Pending")

  const frontierIndex = firstPendingIndex === -1 ? totalSteps - 1 : firstPendingIndex

  const canNavigateTo = (index: number) =>
    index >= 0 && index < totalSteps && (!execution?.requireOrderedSteps || index <= frontierIndex)

  const goToStep = (index: number) => {
    if (!canNavigateTo(index)) return

    setShowNotCompletedForm(false)
    setNotCompletedReason("")
    setCurrentIndex(index)
  }

  const focusAfterResolve = (updated: ChecklistExecution) => {
    const nextPending = updated.steps.findIndex((step) => step.status === "Pending")

    setCurrentIndex(nextPending === -1 ? updated.steps.length - 1 : nextPending)
  }

  const handleComplete = async (value: Partial<Omit<ChecklistExecutionStep, "status">>) => {
    if (!execution || !currentStep) return

    const result = await updateExecutionStep(execution.id, currentStep.id, {
      status: "Completed",
      textValue: value.textValue ?? null,
      numberValue: value.numberValue ?? null,
      dateTimeValue: value.dateTimeValue ?? null,
      selectedOptionLabel: value.selectedOptionLabel ?? null,
      yesNoValue: value.yesNoValue ?? null,
      notes: value.notes ?? null,
    })

    if (!result) return

    setExecution(result)
    onChanged()
    focusAfterResolve(result)
  }

  const handleNotCompleted = async () => {
    if (!execution || !currentStep || !notCompletedReason.trim()) return

    const result = await updateExecutionStep(execution.id, currentStep.id, {
      status: "NotCompleted",
      notCompletedReason,
    })

    if (!result) return

    setExecution(result)
    setShowNotCompletedForm(false)
    setNotCompletedReason("")
    onChanged()
    focusAfterResolve(result)
  }

  const canUndo = (step: ChecklistExecutionStep, index: number) =>
    !isFinalized &&
    step.status !== "Pending" &&
    (!execution?.requireOrderedSteps || !steps.some((s, i) => i > index && s.status !== "Pending"))

  const handleUndo = (step: ChecklistExecutionStep) => {
    if (!execution) return

    setNewConfirmation(t("checklists.run.undoConfirmTitle"), t("checklists.run.undoConfirmMessage"), () => {
      void (async () => {
        const result = await updateExecutionStep(execution.id, step.id, { status: "Pending" })

        if (result) {
          setExecution(result)
          onChanged()
        }
      })()
    })
  }

  const handleFinish = async () => {
    if (!execution) return

    const result = await completeExecution(execution.id)

    if (result) {
      onChanged()
      onClose()
    }
  }

  const hasPendingRequired = steps.some((step) => step.isRequired && step.status === "Pending")

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <section className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={execution?.templateName ?? t("checklists.run.title")}
              icon={faClipboardCheck}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.checklist.checklistRunPopup.button.onClose"
                  />
                </div>
              }
            />

            {execution && (
              <div className="px-3 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-500">
                    {t("checklists.run.stepOf", { current: currentIndex + 1, total: totalSteps })}
                  </span>
                  <span className="text-xs font-bold text-gray-700">{execution.completionPercentage}%</span>
                </div>

                <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-orange-400 to-amber-500 transition-all duration-300"
                    style={{ width: `${execution.completionPercentage}%` }}
                  />
                </div>

                <div className="flex items-center gap-1.5 overflow-x-auto py-3">
                  {steps.map((step, index) => {
                    const navigable = canNavigateTo(index)

                    const isCurrent = index === currentIndex

                    const stateClass =
                      step.status === "Completed"
                        ? "bg-gradient-to-br from-green-400 to-emerald-500 border-emerald-500 text-white shadow-sm shadow-emerald-200"
                        : step.status === "NotCompleted"
                          ? "bg-gradient-to-br from-red-400 to-rose-500 border-rose-500 text-white shadow-sm shadow-rose-200"
                          : isCurrent
                            ? "bg-white border-orange-400 text-orange-600 ring-4 ring-orange-100 scale-110"
                            : navigable
                              ? "bg-white border-gray-300 text-gray-500 hover:border-orange-300"
                              : "bg-gray-100 border-gray-200 text-gray-300 cursor-not-allowed"

                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => goToStep(index)}
                        disabled={!navigable}
                        data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.openStep"
                        className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-200 ${stateClass}`}
                      >
                        {step.status === "Completed" ? (
                          <FontAwesomeIcon icon={faCheck} />
                        ) : step.status === "NotCompleted" ? (
                          <FontAwesomeIcon icon={faClose} />
                        ) : (
                          index + 1
                        )}
                      </button>
                    )
                  })}
                </div>

                {execution.requireOrderedSteps && (
                  <p className="text-xs text-gray-400 italic -mt-1 mb-2">{t("checklists.run.orderedStepsHint")}</p>
                )}
              </div>
            )}

            <div className="px-3 pb-2">
              {currentStep && (
                <ChecklistRunStepCard
                  key={currentStep.id}
                  step={currentStep}
                  showNotCompletedForm={showNotCompletedForm}
                  notCompletedReason={notCompletedReason}
                  onNotCompletedReasonChange={setNotCompletedReason}
                  onRequestNotCompleted={() => setShowNotCompletedForm(true)}
                  onCancelNotCompleted={() => {
                    setShowNotCompletedForm(false)
                    setNotCompletedReason("")
                  }}
                  onConfirmNotCompleted={handleNotCompleted}
                  onComplete={handleComplete}
                  canUndo={canUndo(currentStep, currentIndex)}
                  onUndo={() => handleUndo(currentStep)}
                />
              )}
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className="flex items-center justify-between gap-2 px-3 pb-2">
              <button
                type="button"
                onClick={() => goToStep(currentIndex - 1)}
                disabled={currentIndex === 0}
                data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.previousStep"
                className="flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold text-sm shadow-md shadow-slate-300 transition-all hover:shadow-lg hover:from-slate-700 hover:to-slate-800 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:pointer-events-none disabled:shadow-none disabled:hover:scale-100"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
                {t("checklists.run.previousStep")}
              </button>

              {isLastStep ? (
                !hasPendingRequired &&
                !isFinalized && (
                  <button
                    type="button"
                    onClick={handleFinish}
                    disabled={isLoading}
                    data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.complete"
                    className="flex items-center gap-2 pl-4 pr-3 py-2.5 rounded-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 text-white font-bold text-sm shadow-md shadow-emerald-200 transition-all hover:shadow-lg hover:from-green-600 hover:to-emerald-700 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:pointer-events-none disabled:shadow-none disabled:hover:scale-100"
                  >
                    {t("checklists.run.completeButton")}
                    <FontAwesomeIcon icon={faFlagCheckered} />
                  </button>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => goToStep(currentIndex + 1)}
                  disabled={!canNavigateTo(currentIndex + 1)}
                  data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.nextStep"
                  className="flex items-center gap-2 pl-4 pr-3 py-2.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm shadow-md shadow-orange-200 transition-all hover:shadow-lg hover:from-orange-600 hover:to-amber-600 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:pointer-events-none disabled:shadow-none disabled:hover:scale-100"
                >
                  {t("checklists.run.nextStep")}
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              )}
            </div>
          </section>
        </Animate>
      </div>
    </ModalPortal>
  )
}

interface StepCardProps {
  step: ChecklistExecutionStep
  showNotCompletedForm: boolean
  notCompletedReason: string
  onNotCompletedReasonChange: (value: string) => void
  onRequestNotCompleted: () => void
  onCancelNotCompleted: () => void
  onConfirmNotCompleted: () => void
  onComplete: (value: Partial<Omit<ChecklistExecutionStep, "status">>) => void
  canUndo: boolean
  onUndo: () => void
}

const ChecklistRunStepCard: React.FC<StepCardProps> = ({
  step,
  showNotCompletedForm,
  notCompletedReason,
  onNotCompletedReasonChange,
  onRequestNotCompleted,
  onCancelNotCompleted,
  onConfirmNotCompleted,
  onComplete,
  canUndo,
  onUndo,
}) => {
  const { t, i18n } = useTranslation(["pages", "general"])

  const [textValue, setTextValue] = useState(step.textValue ?? "")

  const [numberValue, setNumberValue] = useState(step.numberValue?.toString() ?? "")

  const [dateTimeValue, setDateTimeValue] = useState(step.dateTimeValue ?? "")

  useEffect(() => {
    setTextValue(step.textValue ?? "")
    setNumberValue(step.numberValue?.toString() ?? "")
    setDateTimeValue(step.dateTimeValue ?? "")
  }, [step.id])

  const isResolved = step.status !== "Pending"

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="0.3s">
      <div
        className={`flex flex-col gap-3 p-4 rounded-2xl border-2 ${
          step.status === "Completed"
            ? "bg-gradient-to-br from-green-50 to-emerald-50 border-emerald-200"
            : step.status === "NotCompleted"
              ? "bg-gradient-to-br from-red-50 to-rose-50 border-rose-200"
              : "bg-white border-orange-200 shadow-sm"
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                step.status === "Completed"
                  ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white"
                  : step.status === "NotCompleted"
                    ? "bg-gradient-to-br from-red-400 to-rose-500 text-white"
                    : "bg-orange-100 text-orange-600"
              }`}
            >
              <FontAwesomeIcon icon={RESPONSE_TYPE_ICON[step.responseType]} />
            </div>

            <div className="min-w-0">
              <span className="font-bold text-slate-800 block truncate">
                {step.title} {step.isRequired && <span className="text-red-500">*</span>}
              </span>
              <span className="text-xs text-slate-500">
                {t(`checklists.templates.responseType${step.responseType}`)}
              </span>
            </div>
          </div>

          {isResolved && (
            <span
              className={`px-2 py-1 text-xs rounded-full font-bold whitespace-nowrap ${
                step.status === "Completed" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {t(`checklists.history.stepStatus${step.status}`)}
            </span>
          )}
        </div>

        {isResolved ? (
          <>
            <p className="text-sm text-slate-700 font-medium bg-white/70 rounded-lg p-2">
              {describeChecklistExecutionStepValue(step, t, t("checklists.run.stepDone"))}
            </p>

            {step.notes && <p className="text-xs text-slate-500 italic">{step.notes}</p>}

            {step.completedByEmployeeName && step.completedAtUtc && (
              <p className="text-xs text-slate-400">
                {step.completedByEmployeeName} ·{" "}
                {formatDateTimeInTimeZone(step.completedAtUtc, i18n.language, companyMemory.timeZoneId)}
              </p>
            )}

            {canUndo && (
              <button
                type="button"
                onClick={onUndo}
                data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.undoStep"
                className="self-start flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-red-500 via-rose-500 to-red-600 text-white font-bold text-sm shadow-md shadow-red-200 transition-all hover:shadow-lg hover:shadow-red-300 hover:scale-105 active:scale-95"
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                {t("checklists.run.undoStep")}
              </button>
            )}
          </>
        ) : (
          <fieldset className="flex flex-col gap-3">
            {!showNotCompletedForm && (
              <>
                {step.responseType === "Confirmation" && (
                  <button
                    type="button"
                    onClick={() => onComplete({})}
                    data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.markDone"
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold shadow-md transition-all hover:shadow-lg hover:scale-[1.01] active:scale-95"
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    {t("checklists.run.markDone")}
                  </button>
                )}

                {step.responseType === "YesNo" && (
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => onComplete({ yesNoValue: true })}
                      data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.yes"
                      className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-bold shadow-md transition-all hover:shadow-lg active:scale-95"
                    >
                      <FontAwesomeIcon icon={faThumbsUp} />
                      {t("general:label_yes")}
                    </button>
                    <button
                      type="button"
                      onClick={() => onComplete({ yesNoValue: false })}
                      data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.no"
                      className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold shadow-md transition-all hover:shadow-lg active:scale-95"
                    >
                      <FontAwesomeIcon icon={faThumbsDown} />
                      {t("general:label_no")}
                    </button>
                  </div>
                )}

                {step.responseType === "MultipleChoice" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {step.availableOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => onComplete({ selectedOptionLabel: option })}
                        data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.selectOption"
                        className="flex items-center gap-2 py-3 px-4 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 font-semibold text-left transition-all hover:border-orange-400 hover:bg-orange-50 hover:text-orange-700 active:scale-95"
                      >
                        <span className="w-2 h-2 rounded-full bg-slate-300 shrink-0" />
                        {option}
                      </button>
                    ))}
                  </div>
                )}

                {step.responseType === "Text" && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <InputField
                        type="text"
                        value={textValue}
                        onChange={(e) => setTextValue(e.target.value)}
                        placeholder={t("checklists.run.textPlaceholder")}
                        descriptionKey="interactionDescriptions:pages.checklist.checklistRunPopup.input.text"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onComplete({ textValue })}
                      disabled={!textValue.trim()}
                      data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.confirmText"
                      className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 text-white flex items-center justify-center shadow-md transition-all hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </div>
                )}

                {step.responseType === "Number" && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <InputField
                        type="number"
                        value={numberValue}
                        onChange={(e) => setNumberValue(e.target.value)}
                        placeholder={t("checklists.run.numberPlaceholder")}
                        descriptionKey="interactionDescriptions:pages.checklist.checklistRunPopup.input.number"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onComplete({ numberValue: Number(numberValue) })}
                      disabled={!numberValue}
                      data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.confirmNumber"
                      className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 text-white flex items-center justify-center shadow-md transition-all hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </div>
                )}

                {step.responseType === "DateTime" && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <InputField
                        type="date"
                        value={dateTimeValue}
                        onChange={(e) => setDateTimeValue(e.target.value)}
                        placeholder={t("checklists.run.dateTimePlaceholder")}
                        descriptionKey="interactionDescriptions:pages.checklist.checklistRunPopup.input.dateTime"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => onComplete({ dateTimeValue })}
                      disabled={!dateTimeValue}
                      data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.confirmDateTime"
                      className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 text-white flex items-center justify-center shadow-md transition-all hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={onRequestNotCompleted}
                  data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.notCompleted"
                  className="self-center flex items-center gap-1.5 text-xs font-bold text-red-500 mt-1 transition-colors hover:text-red-600"
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {t("checklists.run.notCompletedButton")}
                </button>
              </>
            )}

            {showNotCompletedForm && (
              <div className="p-4 rounded-2xl bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 flex flex-col gap-3">
                <div className="flex items-center gap-2 text-red-600 font-bold text-sm">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  {t("checklists.run.notCompletedButton")}
                </div>

                <InputField
                  type="text"
                  value={notCompletedReason}
                  onChange={(e) => onNotCompletedReasonChange(e.target.value)}
                  placeholder={t("checklists.run.notCompletedReasonPlaceholder")}
                  onInvalidMessage={t("checklists.run.notCompletedReasonPlaceholder")}
                  required
                  descriptionKey="interactionDescriptions:pages.checklist.checklistRunPopup.input.notCompletedReason"
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={onConfirmNotCompleted}
                    disabled={!notCompletedReason.trim()}
                    data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.confirmNotCompleted"
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold shadow-md transition-all hover:shadow-lg active:scale-95 disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none"
                  >
                    {t("general:label_confirm")}
                  </button>
                  <button
                    type="button"
                    onClick={onCancelNotCompleted}
                    data-interaction-description-key="interactionDescriptions:pages.checklist.checklistRunPopup.button.cancelNotCompleted"
                    className="flex-1 py-3 rounded-xl bg-white border-2 border-slate-300 text-slate-600 font-bold transition-all hover:bg-slate-50 active:scale-95"
                  >
                    {t("general:label_cancel")}
                  </button>
                </div>
              </div>
            )}
          </fieldset>
        )}
      </div>
    </Animate>
  )
}
